import { encryptData } from "@/features/cryptography";
import { MapDisplayPoint } from "@/features/maps/types";
import { AdaptationType } from "@/types/adaptation";
import { SecretSalt } from "@/types/cryptography";
import { formatAbbreviatedValueWithMetricUnit, normalizeDisplayZero } from "@/utils/number";
import { v4 } from "uuid";

export const autoAdaptationInputSettings: Record<
  Extract<AdaptationType, "low_resilience" | "modern_resilience" | "enhanced_resilience">,
  any
> = {
  low_resilience: {
    tcwThresholdReturnPeriod: 50,
    heatThresholdReturnPeriod: 20,
    windThresholdReturnPeriod: 50,
    heightAboveGround: {
      absolute: 0,
    },
    protection: { fire_protection: "none", foundation_design: "normal" },
  },
  modern_resilience: {
    tcwThresholdReturnPeriod: 200,
    heatThresholdReturnPeriod: 100,
    windThresholdReturnPeriod: 200,
    heightAboveGround: {
      absolute: 0.5,
      relative: {
        additional: 0.5,
        max: 1,
      },
    },
    protection: { fire_protection: "heatEmber", foundation_design: "rigid" },
  },
  enhanced_resilience: {
    tcwThresholdReturnPeriod: 500,
    heatThresholdReturnPeriod: 1000,
    windThresholdReturnPeriod: 500,
    heightAboveGround: {
      absolute: 1,
      relative: {
        additional: 1,
        max: 2,
      },
    },
    protection: {
      fire_protection: "flame",
      foundation_design: "rigid",
    },
  },
};

export const getAdaptationInputsByAdaptationType = ({
  adaptationType,
  baselineInputs,
}: {
  baselineInputs: any;
  adaptationType: AdaptationType;
}) => {
  if (adaptationType === "baseline") {
    return baselineInputs;
  }

  if (
    adaptationType !== "low_resilience" &&
    adaptationType !== "modern_resilience" &&
    adaptationType !== "enhanced_resilience"
  ) {
    return baselineInputs;
  }

  const inputSettings = autoAdaptationInputSettings[adaptationType];
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    user_supplied_id,
    tcwThresholdReturnPeriod,
    heatThresholdReturnPeriod,
    windThresholdReturnPeriod,
    heightAboveGround,
    ...settings
  } = inputSettings;

  let newHeightAboveGround = heightAboveGround?.absolute;

  if (!isNaN(baselineInputs.thresholds.flood.height_above_ground) && heightAboveGround?.relative !== undefined) {
    const { additional, max } = heightAboveGround.relative;
    newHeightAboveGround = baselineInputs.thresholds.flood.height_above_ground + (additional || 0);
    if (newHeightAboveGround > max) {
      newHeightAboveGround = max;
    }
  }

  return {
    ...baselineInputs,
    ...settings,
    thresholds: {
      ...baselineInputs.thresholds,
      flood: {
        height_above_ground: newHeightAboveGround,
      },
      wind: { return_period: windThresholdReturnPeriod },
      heat: { return_period: heatThresholdReturnPeriod },
      cyclone_wind: { return_period: tcwThresholdReturnPeriod },
    },
    item_id: v4(),
  };
};

export const preparePathwayActionInputs = ({
  point,
  adaptationType,
}: {
  point: MapDisplayPoint;
  adaptationType: AdaptationType;
}) => {
  if (!point.asset) return null;

  return getAdaptationInputsByAdaptationType({
    baselineInputs: point.asset.data,
    adaptationType,
  });
};

export type AutoPathwayActionAnalysisParams = {
  adaptation_pathway_id: string;
  encrypted_id: ArrayBuffer;
  data: ArrayBuffer;
};

export const prepareAutoPathwayActionForSupabase = async ({
  secret,
  point,
  autoPathwayId,
  adaptationType,
}: {
  secret: SecretSalt;
  point: MapDisplayPoint;
  autoPathwayId: string;
  adaptationType: AdaptationType;
}) => {
  const analysis = await preparePathwayActionInputs({ point, adaptationType });

  return {
    adaptation_pathway_id: autoPathwayId,
    encrypted_id: await encryptData(secret, analysis.item_id),
    data: await encryptData(secret, JSON.stringify(analysis)),
  } as AutoPathwayActionAnalysisParams;
};

// absolute change measured in percentage points, not percent. Signed: negative means the metric fell.
export const formatAbsoluteChange = (absoluteChange: number | null, unit: "%" | "$") =>
  absoluteChange === null
    ? "–"
    : unit === "%"
      ? `${normalizeDisplayZero(Math.abs(absoluteChange), unit).toFixed(4)} ppts`
      : formatAbbreviatedValueWithMetricUnit(Math.abs(absoluteChange), unit);
